import { useEffect } from 'react'
import { useUnsavedCount } from '@/stores/useSessionGuard'

/**
 * Catches the closed tab and the typed-over URL — the two ways out of Argus that
 * no in-app dialog can intercept.
 *
 * Closing the tab does not itself destroy anything (the session outlives it, and
 * the idle window is two hours), but it is where a user walks away believing the
 * work is filed somewhere. One prompt at that moment is the last chance to say
 * otherwise.
 *
 * Mounted once by `AppShell`.
 */
export default function UnsavedWorkGuard(): null {
  const unsavedCount = useUnsavedCount()

  useEffect(() => {
    // Nothing to lose: leaving the handler attached would prompt on every exit
    // and train users to click straight through it.
    if (unsavedCount === 0) return

    const onBeforeUnload = (event: BeforeUnloadEvent): void => {
      // Browsers show their own generic wording here and ignore any string we
      // provide — the count lives in EphemeralNotice and the sign-out dialog,
      // which are the places we can actually word it ourselves.
      event.preventDefault()
      event.returnValue = ''
    }

    window.addEventListener('beforeunload', onBeforeUnload)
    return () => window.removeEventListener('beforeunload', onBeforeUnload)
  }, [unsavedCount])

  return null
}

export { UnsavedWorkGuard }
